import React, { useState, useEffect, useRef } from "react";
import "../../assets/styles/pages/admin/requests.css";
import AdminSideNav from "../../components/layout/adminSideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import Icon from "../../components/ui/icon";
import moment from "moment-timezone";
import { getTimezoneByCountry } from "../../utils/getTimezoneByCountry";

const AdminRequestsPage = () => {
    const [isSideNavActive, setisSideNavActive] = useState(false);
    const [userInfo, setUserInfo] = useState(
        JSON.parse(localStorage.getItem("AdminInfo")) || {}
    );
    const country = userInfo?.country?.toLowerCase() || "india";
    const [userVerified, setUserVerified] = useState(false);

    const [requests, setrequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [fetchStatus, setFetchStatus] = useState("fetching_first");
    const [skip, setSkip] = useState(0);
    const [hasMore, sethasMore] = useState(true);
    const limit = 20;
    const [isApproving, setisApproving] = useState(null);
    const [isRejecting, setisRejecting] = useState(null);
    const isLoadingMore = useRef(false);

    const showToast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("aToken");
        if (!token) {
            navigate("/auth/login");
            return;
        }

        const fetchUser = async () => {
            if (userVerified) return;
            try {
                const res = await api.get("/api/admin/info");
                setUserInfo(res.data.data);
                setUserVerified(true);
                localStorage.setItem("AdminInfo", JSON.stringify(res.data.data));
                fetchRequests();
            } catch {
                navigate("/auth/login");
            }
        };

        const fetchRequests = async () => {
            if (fetchStatus === "fetching") return;
            setFetchStatus("fetching");

            try {
                const res = await api.get(
                    `/api/a/campaign/requests?limit=${limit}&skip=${skip}${statusFilter !== "all" ? `&status=${statusFilter}` : ""
                    }`
                );
                setrequests((prev) => [...prev, ...res.data.data]);
                setSkip((prev) => prev + limit);
                setFetchStatus("fetched");
                isLoadingMore.current = false;
                if (res.data.data.length < limit) {
                    sethasMore(false);
                    window.removeEventListener("scroll", handleScroll);
                }
            } catch (err) {
                showToast({
                    status: "error",
                    message:
                        err?.response?.data?.message || "Error fetching requests data",
                });
            }
        };

        const handleScroll = () => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const threshold = document.body.offsetHeight - 150;

            if (scrollPosition >= threshold && hasMore) {
                if (fetchStatus === "fetching") return;
                if (fetchStatus === "fetching_first") return;
                if (isLoadingMore.current) return;
                isLoadingMore.current = true;
                fetchRequests();
            }
        };

        fetchUser();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [navigate, showToast, fetchStatus, skip, statusFilter, userVerified, hasMore]);

    const handleStatusChange = async (id, newStatus) => {
        if (isApproving || isRejecting) return;
        if (newStatus === "approve") setisApproving(id);
        if (newStatus === "reject") setisRejecting(id);

        try {
            await api.patch(`/api/a/campaign/a/${id}/${newStatus}`);
            setrequests((prev) =>
                prev.map((r) =>
                    r.id === id ? { ...r, status: newStatus === "approve" ? "approved" : "rejected", updatedAt: Date.now() } : r
                )
            );
            showToast({
                status: "success",
                message: `Request ${newStatus === "approve" ? "approved" : "rejected"} successfully`,
            });
        } catch {
            showToast({
                status: "error",
                message: "Error updating request status",
            });
        } finally {
            setisApproving(null);
            setisRejecting(null);
        }
    };

    const changeFilter = async (status) => {
        if (status === statusFilter) return;
        setStatusFilter(status);
        setrequests([]);
        setSkip(0);
        setFetchStatus("fetching");

        sethasMore(true);

        try {
            const res = await api.get(
                `/api/a/campaign/requests?limit=${limit}&skip=0${status !== "all" ? `&status=${status}` : ""
                }`
            );
            setrequests(res.data.data);
            setSkip(limit);
            setFetchStatus("fetched");
            if (res.data.data.length < limit) sethasMore(false);
        } catch (err) {
            showToast({
                status: "error",
                message:
                    err?.response?.data?.message || "Error fetching requests data",
            });
        }
    };

    return (
        <div className="arequests__page">
            <AdminSideNav
                userName={userInfo.name}
                emailID={userInfo.email}
                active={isSideNavActive}
            />

            <div
                onClick={() => setisSideNavActive(!isSideNavActive)}
                className={isSideNavActive ? "background-shadow active" : "background-shadow"}
            ></div>

            <main>
                <header className="desktop-header">
                    <div>
                        <h2>Requests</h2>
                        <p>Manage and review requests submitted by publishers.</p>
                    </div>
                </header>

                <header className="mobile-header">
                    <div><h2>Requests</h2></div>
                    <button className="nav-btn" onClick={() => setisSideNavActive(!isSideNavActive)}>
                        {isSideNavActive ? <Icon name="XMark" /> : <Icon name="Bars3" />}
                    </button>
                </header>

                <div className="arequests__page">
                    <div className="arequests__filters">
                        {["all", "approved", "pending", "rejected"].map((status) => (
                            <button
                                key={status}
                                className={`arequests__filter-btn ${statusFilter === status ? "active" : ""
                                    }`}
                                onClick={() => changeFilter(status)}
                            >
                                <Icon name="AdjustmentsHorizontal" className="icon" />
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>

                    <div className="requests-container">
                        {requests.length > 0 ? (
                            requests.map((req) => (
                                <div key={req.id} className={`request-card ${req.status}`}>

                                    <div className="info">
                                        <span className={`icon-container ${req.status}`}>
                                            <Icon
                                                name={
                                                    req.status === "pending"
                                                        ? "Clock"
                                                        : req.status === "approved"
                                                            ? "CheckCircle"
                                                            : "XCircle"
                                                }
                                            />
                                        </span>

                                        <span className="message">
                                            {req.status === "pending" && (
                                                <>
                                                    <strong>{req.publisher.name}</strong> requested to join{" "}
                                                    <strong>{req.campaign.name}</strong>{" "}
                                                    {moment(req.createdAt).tz(getTimezoneByCountry(country)).fromNow()}.
                                                </>
                                            )}

                                            {req.status === "approved" && (
                                                <>
                                                    <strong>{req.publisher.name}</strong>’s request to join{" "}
                                                    <strong>{req.campaign.name}</strong> was approved{" "}
                                                    {moment(req.updatedAt).tz(getTimezoneByCountry(country)).fromNow()}.
                                                </>
                                            )}

                                            {req.status === "rejected" && (
                                                <>
                                                    <strong>{req.publisher.name}</strong>’s request to join{" "}
                                                    <strong>{req.campaign.name}</strong> was rejected{" "}
                                                    {moment(req.updatedAt).tz(getTimezoneByCountry(country)).fromNow()}.
                                                </>
                                            )}
                                        </span>
                                    </div>

                                    <div className="actions">
                                        {req.status !== "approved" && (
                                            <button
                                                className="approve-btn"
                                                onClick={() => handleStatusChange(req.id, "approve")}
                                            >
                                                {isApproving === req.id ? "Approving..." : "Approve"}
                                                {isApproving === req.id && (
                                                    <Icon name="ArrowPath" className="spin" />
                                                )}
                                            </button>
                                        )}
                                        {req.status !== "rejected" && (
                                            <button
                                                className="reject-btn"
                                                onClick={() => handleStatusChange(req.id, "reject")}
                                            >
                                                {isRejecting === req.id ? "Rejecting..." : "Reject"}
                                                {isRejecting === req.id && (
                                                    <Icon name="ArrowPath" className="spin" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : fetchStatus !== "fetching_first" && fetchStatus !== "fetching" ? (
                            <div className="requests-no-data">
                                <Icon name="Inbox" className="icon" /> No requests found
                            </div>
                        ) : (
                            ""
                        )}

                        {(fetchStatus === "fetching_first" || fetchStatus === "fetching") &&
                            [...Array(5)].map((_, i) => (
                                <div className="requests-card loading" key={i}>
                                    <div className="skeleton"></div>
                                    <div className="skeleton small"></div>
                                </div>
                            ))}
                    </div>
                </div>

                <span className="copyright-text">© 2025 Swift Digital Ads, All rights reserved</span>
            </main>
        </div>
    );
};

export default AdminRequestsPage;